import GlobalWrapper from '../../../../components/Wrapper/GlobalWrapper';
import { Button, Chip, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import BorderAllIcon from '@mui/icons-material/BorderAll';
import { downloadCSV, extractCSV, makeCSV } from '../../../../components/CustomTableWithPagination/csvExport';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import styled from 'styled-components';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import GlobalTableNoPagination from '../../../../components/CustomTableWithPagination/GlobalTableNoPagination';
import { AddCsvExtraLeadExpenseTableHeader } from '../utils/AddCsvExtraLeadExpenseTableHeader';
import GlobalDialog from '../../../../components/mui/Dialogue/GlobalDialog';
import EditCSVData from './EditCSVData';
import { useDispatch } from 'react-redux';
import { getOwnerList, getSalaryAndBonus, postSalaryOrRBonusAndSync } from '../../../../redux/features/CRDslice';
import { useSelector } from 'react-redux';
import useHandleApiResponse from '../../../../Hooks/useHandleApiResponse';
import SyncOgaCost from './SyncOgaCost';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

export default function AddExtraForLeadExpense({ pageType }) {
  const [month, setMonth] = useState(null);
  const [year, setYear] = useState(null);
  const [monthFilter, setMonthFilter] = useState(dayjs().month());
  const [yearFilter, setYearFilter] = useState(dayjs().year());
  const [sheet, setSheet] = useState(null);
  const [refresh, setRefresh] = useState(false);

  const [csvFileName, setCsvFileName] = useState(false);
  const [modalState, setModalState] = useState(false);
  const [campaignState, setCampaignState] = useState({
    campaign_amount: null,
    total_leads: null,
    total_qualified: null,
  });
  const [tableState, setTableState] = useState({
    page: 1,
    pageSize: 50,
    search: '',
    frozenColumns: [],
    hiddenColumns: [],
  });

  const salaryAndBonusTable = useSelector((state) => state.crd.salaryAndBonus.data);
  const salaryAndBonusTableLoading = useSelector((state) => state.crd.salaryAndBonus.loading);
  const ownerList = useSelector((state) => state.crd.ownerList.data);
  const ownerListLoading = useSelector((state) => state.crd.ownerList.loading);
  const submitLoading = useSelector((state) => state.crd.submitLoading);

  const isNotDM = useMemo(() => pageType !== 'campaign', [pageType]);

  const dispatch = useDispatch();
  const handleApiHelper = useHandleApiResponse();

  const handleButtonActionClick = useCallback((actionKey, row = {}, actionType) => {
    if (!actionKey) return;
    else {
      setModalState({
        actionKey,
        row,
      });
    }
  }, []);

  const modalConfigs = useMemo(
    () => [
      {
        actionKey: 'edit',
        title: 'Edit',
        maxWidth: 'md',
        render: ({ row }) => (
          <EditCSVData
            row={row}
            pageType={pageType}
            month={monthFilter}
            year={yearFilter}
            setOpenModal={setModalState}
            setRefresh={setRefresh}
            submitLoading={submitLoading}
          />
        ),
      },
      {
        actionKey: 'sync',
        title: 'Calculate OGA Cost',
        maxWidth: 'xs',
        render: ({ row }) => <SyncOgaCost row={row} setOpenModal={setModalState} submitLoading={submitLoading} />,
      },
    ],
    [submitLoading],
  );
  const activeConfig = useMemo(() => {
    if (!Array.isArray(modalConfigs) || !modalState.actionKey || !modalState.row) return null;
    return modalConfigs.find((c) => c?.actionKey === modalState.actionKey) || null;
  }, [modalConfigs, modalState.actionKey, modalState.row]);

  useEffect(() => {
    const salaryAndBonusPromise = dispatch(getSalaryAndBonus({ year: yearFilter, month: Number(monthFilter) }));

    return () => {
      salaryAndBonusPromise.abort();
    };
  }, [monthFilter, yearFilter, dispatch, refresh]);

  useEffect(() => {
    const ownerSignel = dispatch(getOwnerList());

    return () => {
      ownerSignel.abort();
    };
  }, [dispatch]);

  const submitHandler = async () => {
    let data = {};
    if (isNotDM) {
      let extractedData = await extractCSV(['id', 'name', pageType], sheet, {
        skipFirstRow: true,
      });
      data = {
        year,
        month,
        data: extractedData,
        path: pageType,
      };
    } else {
      data = {
        ...campaignState,
        month,
        year,
        path: pageType,
      };
    }

    dispatch(postSalaryOrRBonusAndSync(data)).then((res) =>
      handleApiHelper(res, {
        onSuccess: () => {
          if (isNotDM) setCsvFileName('');
          setSheet(null);
          setRefresh((prev) => !prev);
          setMonth(null);
          setYear(null);
        },
      }),
    );
  };

  const downloadSampleCSV = () => {
    const headers = [
      { headerName: 'ID', field: 'id' },
      { headerName: 'Name', field: 'name' },
      { headerName: pageType === 'bonus' ? 'Bonus' : 'Salary' },
    ];
    const csvString = makeCSV({ headers, rows: ownerList });
    downloadCSV(csvString, 'sample.csv');
  };
  const importCSV = async (e) => {
    const file = e.target.files[0];
    setCsvFileName(file?.name || '');
    setSheet(file || null);
    e.target.value = null;
  };
  return (
    <GlobalWrapper title={`Add ${pageType === 'bonus' ? 'Bonus' : pageType === 'salary' ? 'Salary' : 'Campaign'}`}>
      <Stack gap={2}>
        <Stack direction={'row'} gap={1} sx={{ alignSelf: 'flex-end' }}>
          {pageType === 'bonus' && (
            <Button variant="outlined" onClick={() => handleButtonActionClick('sync')}>
              Calculate Cost
            </Button>
          )}
          {isNotDM && (
            <Button
              startIcon={ownerListLoading ? <CircularProgress size={'20px'} /> : <BorderAllIcon />}
              variant="contained"
              color="success"
              onClick={downloadSampleCSV}
              disabled={ownerListLoading}
            >
              Download Template
            </Button>
          )}
        </Stack>

        <Stack
          gap={2}
          sx={{
            p: 2,
            border: '1px solid #dbeaff',
            borderRadius: 2,
          }}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={'center'} gap={2}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Month"
                maxDate={dayjs()}
                views={['month']}
                sx={{ width: '100%', maxWidth: { xs: '100%', sm: 250 } }}
                value={month ? dayjs(month) : null}
                onChange={(v) => {
                  if (v === null) {
                    setMonth(null);
                  } else if (v.isValid()) {
                    setMonth(v.format('MM'));
                  }
                }}
              />
              <DatePicker
                label="Year"
                views={['year']}
                maxDate={dayjs()}
                sx={{ width: '100%', maxWidth: { xs: '100%', sm: 250 } }}
                value={year ? dayjs(year) : null}
                onChange={(v) => {
                  if (v === null) {
                    setYear(null);
                  } else if (v.isValid()) {
                    setYear(v.format('YYYY'));
                  }
                }}
              />
            </LocalizationProvider>
          </Stack>

          {pageType === 'campaign' ? (
            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={'center'} gap={2}>
              <TextField
                type="number"
                label="Total Amount"
                onWheel={(e) => e.target.blur()}
                value={campaignState.campaign_amount || ''}
                disabled={!month || !year}
                sx={{ maxWidth: { xs: '100%', sm: 250 }, width: '100%' }}
                onChange={(e) => setCampaignState((prev) => ({ ...prev, campaign_amount: e.target.value }))}
              />
              <TextField
                type="number"
                label="Total Lead"
                onWheel={(e) => e.target.blur()}
                disabled={!month || !year}
                value={campaignState.total_leads || ''}
                sx={{ maxWidth: { xs: '100%', sm: 250 }, width: '100%' }}
                onChange={(e) => setCampaignState((prev) => ({ ...prev, total_leads: e.target.value }))}
              />
              <TextField
                type="number"
                label="Total Qualified"
                onWheel={(e) => e.target.blur()}
                disabled={!month || !year}
                value={campaignState.total_qualified || ''}
                sx={{ maxWidth: { xs: '100%', sm: 250 }, width: '100%' }}
                onChange={(e) => setCampaignState((prev) => ({ ...prev, total_qualified: e.target.value }))}
              />
            </Stack>
          ) : (
            <Stack direction={'row'} alignItems={'center'} gap={2}>
              <Typography variant="h6">Import Data :</Typography>
              <Button
                variant="outlined"
                component="label"
                startIcon={csvFileName ? <SyncAltIcon /> : <FileUploadIcon />}
                disabled={!month || !year}
              >
                {csvFileName ? 'Change' : 'Import'}
                <VisuallyHiddenInput type="file" onChange={importCSV} />
              </Button>
              {Boolean(csvFileName) && (
                <Chip
                  variant="filled"
                  label={csvFileName}
                  onDelete={() => {
                    setCsvFileName('');
                    setSheet(null);
                  }}
                />
              )}
            </Stack>
          )}
          <Button
            variant="contained"
            disabled={submitLoading || (isNotDM ? !csvFileName : !Object.values(campaignState).every(Boolean))}
            onClick={submitHandler}
          >
            {submitLoading ? 'Submitting...' : 'Submit'}
          </Button>
        </Stack>
        {isNotDM && (
          <>
            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={'center'} gap={2}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Month"
                  views={['month']}
                  sx={{ width: '100%', maxWidth: { xs: '100%', sm: 250 } }}
                  value={monthFilter ? dayjs().month(Number(monthFilter) - 1) : null}
                  onChange={(v) => {
                    if (v === null) {
                      setMonthFilter(null);
                    } else if (v.isValid()) {
                      setMonthFilter(v.format('MM'));
                    }
                  }}
                />
                <DatePicker
                  label="Year"
                  views={['year']}
                  sx={{ width: '100%', maxWidth: { xs: '100%', sm: 250 } }}
                  value={yearFilter ? dayjs().year(yearFilter) : null}
                  onChange={(v) => {
                    if (v === null) {
                      setYearFilter(null);
                    } else if (v.isValid()) {
                      setYearFilter(v.format('YYYY'));
                    }
                  }}
                />
              </LocalizationProvider>
            </Stack>
            <GlobalTableNoPagination
              headers={AddCsvExtraLeadExpenseTableHeader({ pageType })}
              rows={salaryAndBonusTable}
              loading={salaryAndBonusTableLoading}
              tableState={tableState}
              setTableState={setTableState}
              rowUniqueId="user_cost_id"
              // onButtonActionClick={(actionKey, row, actionType) => setOpenModal({ row })}
              onButtonActionClick={handleButtonActionClick}
            />
            <GlobalDialog
              title={activeConfig?.title}
              open={Boolean(modalState?.actionKey)}
              setOpen={(val) => {
                if (!val) setModalState({ actionKey: '', row: null });
              }}
              closeButton={true}
              content={activeConfig?.render ? activeConfig.render({ row: modalState.row }) : null}
              maxWidth={activeConfig?.maxWidth || 'md'}
              fullWidth={true}
            />
          </>
        )}
      </Stack>
    </GlobalWrapper>
  );
}
